// Emplacements pixels publicitaires. Les IDs sont désormais configurables
// depuis l'admin (/admin/integrations) et chargés au runtime via /api/public/settings.
// Repli possible sur les variables de build REACT_APP_*.
import { api } from "@/lib/api";
import { isPrivatePath } from "@/lib/media";

// Clé de stockage du consentement cookies (RGPD). Valeurs : "accepted" | "refused".
export const CONSENT_KEY = "pp_cookie_consent";

export function consentGiven() {
  try {
    return localStorage.getItem(CONSENT_KEY) === "accepted";
  } catch (e) {
    return false;
  }
}

// Les identifiants viennent des réglages admin : on n'accepte que des caractères sûrs
// (anti-injection de script) et on utilise textContent (jamais innerHTML).
const SAFE_PIXEL_ID = /^[A-Za-z0-9_-]{1,64}$/;

function injectMeta(meta) {
  if (!meta || window.fbq || !SAFE_PIXEL_ID.test(meta)) return;
  const s = document.createElement("script");
  s.textContent = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${meta}');fbq('track','PageView');`;
  document.head.appendChild(s);
}

function injectTiktok(tiktok) {
  if (!tiktok || window.ttq || !SAFE_PIXEL_ID.test(tiktok)) return;
  const s = document.createElement("script");
  s.textContent = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.load=function(e){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]={};var o=d.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e;var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${tiktok}');ttq.page()}(window,document,'ttq');`;
  document.head.appendChild(s);
}

// Libellé de conversion Google Ads « réservation terminée » (AW-XXXX/YYYY), lu au runtime.
let googleConversionLabel = "";
// Libellé de conversion Google Ads « Envoi de formulaire de lead » (valeur 1 €).
const DEFAULT_GOOGLE_LEAD_LABEL = "AW-18507201999/VRvvCK7gqZkdEM_79fhE";
let googleLeadConversionLabel = "";
const SAFE_ID = /^[A-Za-z0-9_\-\/]+$/;

function injectGoogle(google, ...conversionLabels) {
  const awIds = conversionLabels.filter(Boolean).map((l) => l.split("/")[0]).filter(Boolean);
  const primary = google || awIds[0];
  if (!primary || window.gtag) return;
  const ids = [primary];
  awIds.forEach((aw) => { if (!ids.includes(aw)) ids.push(aw); });
  if (!ids.every((i) => SAFE_ID.test(i))) return;
  const lib = document.createElement("script");
  lib.async = true;
  lib.src = `https://www.googletagmanager.com/gtag/js?id=${primary}`;
  document.head.appendChild(lib);
  const s = document.createElement("script");
  s.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());${ids.map((i) => `gtag('config','${i}');`).join("")}`;
  document.head.appendChild(s);
}

function injectPinterest(tagId) {
  if (!tagId || window.pintrk || !SAFE_ID.test(tagId)) return;
  const s = document.createElement("script");
  s.textContent = `!function(e){if(!window.pintrk){window.pintrk=function(){window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var n=window.pintrk;n.queue=[],n.version="3.0";var t=document.createElement("script");t.async=!0,t.src=e;var r=document.getElementsByTagName("script")[0];r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");pintrk('load','${tagId}');pintrk('page');`;
  document.head.appendChild(s);
}

// Microsoft Clarity : JAMAIS sur l'admin / l'espace auto-école (pages privées).
let clarityId = "";
let clarityStopped = false;

function injectClarity(clarity) {
  if (!clarity || !SAFE_PIXEL_ID.test(clarity)) return;
  clarityId = clarity;
  if (window.clarity || isPrivatePath(window.location.pathname)) return;
  const s = document.createElement("script");
  s.textContent = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${clarity}");`;
  document.head.appendChild(s);
}

// Appelé à chaque changement de page (SPA) : stop sur les pages privées, reprise ailleurs.
export function syncClarityRoute(pathname) {
  const priv = isPrivatePath(pathname);
  if (priv) {
    if (window.clarity && !clarityStopped) {
      try { window.clarity("stop"); } catch (e) { /* ignore */ }
      clarityStopped = true;
    }
    return;
  }
  if (!clarityId || !consentGiven()) return;
  if (!window.clarity) { injectClarity(clarityId); return; }
  if (clarityStopped) {
    try { window.clarity("start"); } catch (e) { /* ignore */ }
    clarityStopped = false;
  }
}

export async function initPixels() {
  // RGPD : n'injecter les pixels QUE si l'utilisateur a explicitement accepté.
  if (!consentGiven()) return;
  let cfg = {};
  try {
    const { data } = await api.get("/public/settings");
    cfg = data || {};
  } catch (e) {
    cfg = {};
  }
  injectMeta(cfg.meta_pixel_id || process.env.REACT_APP_META_PIXEL_ID);
  injectTiktok(cfg.tiktok_pixel_id || process.env.REACT_APP_TIKTOK_PIXEL_ID);
  googleConversionLabel = (cfg.google_ads_conversion_label || process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || "").trim();
  googleLeadConversionLabel = (cfg.google_ads_lead_conversion_label !== undefined
    ? cfg.google_ads_lead_conversion_label
    : (process.env.REACT_APP_GOOGLE_ADS_LEAD_CONVERSION_LABEL || DEFAULT_GOOGLE_LEAD_LABEL)).trim();
  if (googleLeadConversionLabel && !SAFE_ID.test(googleLeadConversionLabel)) googleLeadConversionLabel = "";
  injectGoogle(cfg.google_ads_id || process.env.REACT_APP_GOOGLE_ADS_ID, googleConversionLabel, googleLeadConversionLabel);
  injectPinterest(cfg.pinterest_tag_id || process.env.REACT_APP_PINTEREST_TAG_ID);
  injectClarity(cfg.clarity_id || process.env.REACT_APP_CLARITY_ID);
}

// PROSPECT (Lead) — envoyé à TOUS les pixels (Meta « Lead », TikTok « SubmitForm », GA4
// « generate_lead », Pinterest « lead ») + conversion Google Ads lead (via trackLeadFormConversion).
// UNE SEULE FOIS par envoi de formulaire (garde localStorage + eventID pour la déduplication Meta/TikTok).
// source : "reserver" | "contact" | "waitlist" ; refId : id de la réservation / du message / de l'inscription.
export function trackLead(refId, source = "reserver") {
  if (!refId) return;
  const key = `pp_lead_sent_${source}_${refId}`;
  let already = false;
  try { already = !!localStorage.getItem(key); } catch (e) { /* ignore */ }
  if (!already) {
    const pixels = [];
    const eventId = `lead_${source}_${refId}`;
    if (window.fbq) {
      window.fbq("track", "Lead", { content_category: source }, { eventID: eventId });
      pixels.push("meta");
    }
    if (window.ttq) {
      window.ttq.track("SubmitForm", { content_name: source }, { event_id: eventId });
      pixels.push("tiktok");
    }
    if (window.gtag) {
      window.gtag("event", "generate_lead", { lead_source: source, transaction_id: eventId });
      pixels.push("google");
    }
    if (window.pintrk) {
      window.pintrk("track", "lead", { lead_type: source, event_id: eventId });
      pixels.push("pinterest");
    }
    if (pixels.length) {
      try { localStorage.setItem(key, "1"); } catch (e) { /* ignore */ }
      api.post("/public/pixel-event", {
        lead_id: null, event: "Lead", pixels, source, ref_id: refId,
        reservation_id: source === "reserver" ? refId : null,
      }).catch(() => {});
    }
  }
  // Conversion Google Ads « lead » (1 €), elle-même dédupliquée.
  trackLeadFormConversion(refId, source);
}

// Conversion Google Ads « Envoi de formulaire de lead » (1 €) — formulaires réservation, contact
// et liste d'attente. Envoyée UNE SEULE FOIS par envoi (garde localStorage + transaction_id pour
// la déduplication Google) et enregistrée côté serveur pour le « Suivi publicité ».
// source : "reserver" | "contact" | "waitlist" ; refId : id de la réservation / du message / de l'inscription.
export function trackLeadFormConversion(refId, source = "reserver") {
  if (!window.gtag || !googleLeadConversionLabel || !refId) return;
  const key = `pp_lead_conv_sent_${source}_${refId}`;
  try {
    if (localStorage.getItem(key)) return;
  } catch (e) { /* ignore */ }
  window.gtag("event", "conversion", {
    send_to: googleLeadConversionLabel,
    value: 1.0,
    currency: "EUR",
    transaction_id: `${source}_${refId}`,
  });
  try { localStorage.setItem(key, "1"); } catch (e) { /* ignore */ }
  api.post("/public/pixel-event", {
    event: "GoogleLeadConversion", pixels: ["google"], source, ref_id: refId,
    reservation_id: source === "reserver" ? refId : null, value: 1,
  }).catch(() => {});
}

// --- Entonnoir de réservation (parcours élève) ---

// Début de réservation / sélection d'un forfait : InitiateCheckout.
export function trackReservationStart({ formula, value, currency = "EUR" } = {}) {
  const pixels = [];
  if (window.fbq) {
    window.fbq("track", "InitiateCheckout", { content_name: formula, value: value || 0, currency });
    pixels.push("meta");
  }
  if (window.ttq) {
    window.ttq.track("InitiateCheckout", { content_type: "product", content_name: formula, value: value || 0, currency });
    pixels.push("tiktok");
  }
  if (window.gtag) {
    window.gtag("event", "begin_checkout", { value: value || 0, currency });
    pixels.push("google");
  }
  if (pixels.length) {
    api.post("/public/pixel-event", { event: "InitiateCheckout", pixels, value: value || null }).catch(() => {});
  }
}

// Acompte payé : Purchase (avec la valeur de l'acompte réglé).
export function trackPurchase({ reservationId, value, currency = "EUR" } = {}) {
  const pixels = [];
  if (window.fbq) {
    window.fbq("track", "Purchase", { value: value || 0, currency });
    pixels.push("meta");
  }
  if (window.ttq) {
    window.ttq.track("CompletePayment", { value: value || 0, currency });
    pixels.push("tiktok");
  }
  if (window.gtag) {
    window.gtag("event", "purchase", { value: value || 0, currency, transaction_id: reservationId || undefined });
    pixels.push("google");
  }
  if (window.pintrk) {
    window.pintrk("track", "checkout", { value: value || 0, currency, order_id: reservationId || undefined });
    pixels.push("pinterest");
  }
  if (pixels.length) {
    api.post("/public/pixel-event", { reservation_id: reservationId || null, event: "Purchase", pixels, value: value || null }).catch(() => {});
  }
}

// Fin de réservation (dossier complet : documents obligatoires déposés) : Schedule.
// C'est l'événement d'optimisation des campagnes Meta (on cherche des gens qui réservent).
// Envoyé UNE SEULE FOIS par réservation (garde en localStorage + eventID = id de réservation
// pour la déduplication côté Meta).
export function trackSchedule({ reservationId, formula, value, city, currency = "EUR" } = {}) {
  if (!reservationId) return;
  const key = `pp_schedule_sent_${reservationId}`;
  try {
    if (localStorage.getItem(key)) return;
  } catch (e) { /* stockage indisponible : on envoie quand même */ }
  const pixels = [];
  if (window.fbq) {
    window.fbq("track", "Schedule",
      { content_name: formula || undefined, content_category: city || undefined, value: value || 0, currency },
      { eventID: `schedule_${reservationId}` });
    pixels.push("meta");
  }
  if (window.ttq) {
    window.ttq.track("Schedule", { content_name: formula, value: value || 0, currency },
      { event_id: `schedule_${reservationId}` });
    pixels.push("tiktok");
  }
  if (window.gtag) {
    // Google Ads ne compte une conversion qu'avec son libellé « send_to » (AW-XXX/YYY).
    if (googleConversionLabel) {
      window.gtag("event", "conversion", {
        send_to: googleConversionLabel, value: value || 0, currency, transaction_id: reservationId,
      });
    }
    // Événement GA4 (importable comme conversion dans Google Analytics)
    window.gtag("event", "schedule", { value: value || 0, currency, transaction_id: reservationId });
    pixels.push("google");
  }
  if (window.pintrk) {
    window.pintrk("track", "signup", { lead_type: "reservation", event_id: `schedule_${reservationId}` });
    pixels.push("pinterest");
  }
  // Marqué « envoyé » uniquement si au moins un pixel était actif (consentement donné).
  if (pixels.length) {
    try { localStorage.setItem(key, "1"); } catch (e) { /* ignore */ }
    api.post("/public/pixel-event", { reservation_id: reservationId, event: "Schedule", pixels, value: value || null }).catch(() => {});
  }
}
